// Classic Ludo King-style board. Each base = ONE large colored square with a WHITE inner
// box and 4 colored dot-slots. Tokens use map-pin / location-marker style.
// Per-player dice show next to each base header.
import React from 'react';
import { motion } from 'framer-motion';
import { OUTER_PATH, YELLOW_HOME, GREEN_HOME, RED_HOME, BLUE_HOME, CENTER, squareType, tokenCoord, COLOR_LABELS } from './ludoEngine';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';

// Per-color theme — extruded base + map-pin paint.
const COLOR_THEME = {
    yellow: { base: '#FFF200', baseDark: '#C9A800', pin: '#FFD400', pinDark: '#A07A00', name: 'You' },
    green:  { base: '#10B981', baseDark: '#065F46', pin: '#10B981', pinDark: '#064E3B', name: 'Bot 1' },
    red:    { base: '#E31E24', baseDark: '#7F1010', pin: '#E31E24', pinDark: '#7F1010', name: 'Bot 2' },
    blue:   { base: '#3B82F6', baseDark: '#1E3A8A', pin: '#3B82F6', pinDark: '#1E3A8A', name: 'Bot 3' },
};

const SQUARE_STAR = ['star', 'safe'];

function classNames(...xs) { return xs.filter(Boolean).join(' '); }

export default function LudoBoard({ tokens, highlightedTokens, onTokenClick, lastMovedToken, activeColors }) {
    const isActive = (color) => activeColors.includes(color);

    // 15x15 grid. We render only NON-base cells via grid. Bases render as 4 overlay squares.
    const grid = new Map();
    const setCell = (r, c, info) => grid.set(`${r},${c}`, info);

    OUTER_PATH.forEach(([r, c], idx) => setCell(r, c, { type: 'outer', outerIdx: idx }));
    YELLOW_HOME.forEach(([r, c], idx) => setCell(r, c, { type: 'home', color: 'yellow', homeIdx: idx }));
    GREEN_HOME.forEach(([r, c], idx) => setCell(r, c, { type: 'home', color: 'green', homeIdx: idx }));
    RED_HOME.forEach(([r, c], idx) => setCell(r, c, { type: 'home', color: 'red', homeIdx: idx }));
    BLUE_HOME.forEach(([r, c], idx) => setCell(r, c, { type: 'home', color: 'blue', homeIdx: idx }));
    setCell(CENTER[0], CENTER[1], { type: 'center' });

    const cells = [];
    for (let r = 0; r < 15; r++) for (let c = 0; c < 15; c++) {
        // Skip base regions in the grid — they'll be drawn as overlays
        if ((r >= 9 && c <= 5) || (r <= 5 && c <= 5) || (r <= 5 && c >= 9) || (r >= 9 && c >= 9)) {
            cells.push({ r, c, type: 'base-cell' });
            continue;
        }
        const info = grid.get(`${r},${c}`) || { type: 'empty' };
        cells.push({ r, c, ...info });
    }

    return (
        <div className="relative w-full aspect-square bg-white rounded-2xl sm:rounded-3xl shadow-2xl ring-2 ring-slate-900/10 overflow-hidden" data-testid="ludo-board">
            {/* 15x15 grid */}
            <div className="absolute inset-0 grid gap-0" style={{ gridTemplateColumns: 'repeat(15, 1fr)', gridTemplateRows: 'repeat(15, 1fr)' }}>
                {cells.map((cell) => (
                    <Cell key={`${cell.r},${cell.c}`} cell={cell} active={cell.color ? isActive(cell.color) : true} />
                ))}
            </div>

            {/* 4 Base overlays — base pins become clickable when highlighted (rolled 6) */}
            <BaseOverlay color="green"  active={isActive('green')}  pos="top-left"     tokens={tokens.green  || [0,0,0,0]} highlightedTokens={highlightedTokens} onTokenClick={onTokenClick} />
            <BaseOverlay color="red"    active={isActive('red')}    pos="top-right"    tokens={tokens.red    || [0,0,0,0]} highlightedTokens={highlightedTokens} onTokenClick={onTokenClick} />
            <BaseOverlay color="yellow" active={isActive('yellow')} pos="bottom-left"  tokens={tokens.yellow || [0,0,0,0]} highlightedTokens={highlightedTokens} onTokenClick={onTokenClick} />
            <BaseOverlay color="blue"   active={isActive('blue')}   pos="bottom-right" tokens={tokens.blue   || [0,0,0,0]} highlightedTokens={highlightedTokens} onTokenClick={onTokenClick} />

            {/* Center diamond */}
            <CenterStar activeColors={activeColors} />

            {/* Tokens on track */}
            <TokensLayer tokens={tokens} activeColors={activeColors} highlightedTokens={highlightedTokens} onTokenClick={onTokenClick} lastMovedToken={lastMovedToken} />
        </div>
    );
}

// ---- Cell (outer / home / center / empty) ----
function Cell({ cell }) {
    const { type, outerIdx, homeIdx, color } = cell;

    if (type === 'base-cell') return <div />;

    if (type === 'home') {
        const t = COLOR_THEME[color];
        return (
            <div className="flex items-center justify-center"
                style={{ background: t.base, borderRight: '1px solid rgba(0,0,0,0.15)', borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
                {homeIdx === 4 && <span className="text-white font-bold text-[8px] sm:text-[10px]">★</span>}
            </div>
        );
    }

    // Center cell MUST occupy its grid slot (otherwise CSS grid auto-flow shifts every
    // subsequent cell one column to the left — that's what made YELLOW_HOME render at
    // col 6 instead of col 7). Render an invisible placeholder.
    if (type === 'center') return <div aria-hidden="true" />;

    if (type === 'outer') {
        const t = squareType(outerIdx);
        const startColor = ({ 0: 'yellow', 13: 'green', 26: 'red', 39: 'blue' })[outerIdx];
        // Classic Ludo: start cell stays WHITE with only a small colored marker (so the home
        // column is the ONLY coloured stripe leading toward the centre and looks symmetric).
        return (
            <div className="relative flex items-center justify-center"
                style={{ background: '#ffffff', borderRight: '1px solid rgba(0,0,0,0.15)', borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
                {SQUARE_STAR.includes(t) && !startColor && (
                    <svg viewBox="0 0 24 24" className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 fill-none stroke-current" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                )}
                {/* Symmetric colored star marker — NO directional gradient (keeps the
                    only colored stripe in each arm = the centered home column itself). */}
                {startColor && (
                    <div className="relative z-10 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: COLOR_THEME[startColor].baseDark }}>
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                                fill={COLOR_THEME[startColor].base} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                    </div>
                )}
            </div>
        );
    }

    // Empty cell (shouldn't happen for non-base cells, but guard)
    return <div style={{ borderRight: '1px solid rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.08)' }} />;
}

// Simple bouncing hand pointer — used by LudoDice to indicate "tap to roll"
export function HandPointer({ size = 36 }) {
    return (
        <motion.div
            animate={{ y: [0, 4, 8, 4, 0] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
            className="text-amber-400 drop-shadow-lg pointer-events-none"
            style={{ fontSize: size }}
        >👇</motion.div>
    );
}

function ArrowEntry({ dir, color }) {
    const Icon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : dir === 'left' ? ArrowLeft : ArrowRight;
    return <Icon className="w-3 h-3 sm:w-4 sm:h-4" style={{ color, strokeWidth: 3 }} />;
}

// ---- Base overlay: large colored square with white inner + 4 token-parking pins ----
function BaseOverlay({ color, active, pos, tokens, highlightedTokens, onTokenClick }) {
    const t = COLOR_THEME[color];
    const positions = {
        'top-left':     { left: '0%', top: '0%' },
        'top-right':    { left: '60%', top: '0%' },
        'bottom-left':  { left: '0%', top: '60%' },
        'bottom-right': { left: '60%', top: '60%' },
    };
    const p = positions[pos];
    const inBaseCount = tokens.filter((tp) => tp === 0).length;
    const isHighlightedHere = (i) =>
        highlightedTokens?.color === color &&
        highlightedTokens?.indices?.includes(i) &&
        tokens[i] === 0;
    return (
        <div className="absolute" style={{
            left: p.left, top: p.top, width: '40%', height: '40%',
            background: t.base,
            opacity: active ? 1 : 0.35,
            filter: active ? 'none' : 'grayscale(40%)',
        }}>
            {/* Inner white square — clean parking area */}
            <div className="absolute" style={{
                left: '15%', top: '15%', width: '70%', height: '70%',
                background: '#ffffff',
                borderRadius: '6%',
                boxShadow: 'inset 0 2px 0 rgba(0,0,0,0.08), 0 2px 0 rgba(0,0,0,0.06)',
            }}>
                {/* 4 parking slots (2x2). Tokens still in base render as the SAME map-pin
                    shape as on-track tokens — only sized to fit the parking square.
                    When highlighted (player rolled 6) the slot becomes a clickable button. */}
                <div className="absolute inset-[10%] grid grid-cols-2 grid-rows-2 gap-[8%]">
                    {[0, 1, 2, 3].map((i) => {
                        const filled = tokens[i] === 0;
                        const highlighted = isHighlightedHere(i);
                        const handleClick = highlighted && onTokenClick ? () => onTokenClick(color, i) : undefined;
                        return (
                            <div key={i} className="relative flex items-end justify-center">
                                {filled && (
                                    <motion.button
                                        type="button"
                                        onClick={handleClick}
                                        disabled={!highlighted}
                                        data-testid={`ludo-base-pin-${color}-${i}`}
                                        animate={
                                            highlighted
                                                ? { y: [-3, 3, -3], scale: [1, 1.12, 1] }
                                                : (active && inBaseCount > 0 ? { y: [-2, 2, -2], rotate: [-3, 3, -3] } : {})
                                        }
                                        transition={{
                                            repeat: Infinity,
                                            duration: highlighted ? 0.7 : (1.4 + i * 0.13),
                                            ease: 'easeInOut',
                                        }}
                                        className={classNames(
                                            'w-[90%] h-[95%] p-0 bg-transparent border-0 outline-none relative',
                                            highlighted && 'cursor-pointer z-30'
                                        )}
                                        style={highlighted ? { filter: 'drop-shadow(0 0 6px #fbbf24)' } : undefined}
                                    >
                                        <MapPin color={color} highlighted={highlighted} isLast={false} />
                                        {highlighted && (
                                            <motion.span
                                                animate={{ y: [-3, 2, -3] }}
                                                transition={{ repeat: Infinity, duration: 0.9 }}
                                                className="absolute -top-3 left-1/2 -translate-x-1/2 text-amber-400 text-base sm:text-lg drop-shadow pointer-events-none"
                                                data-testid={`ludo-hand-base-${color}-${i}`}
                                            >👇</motion.span>
                                        )}
                                    </motion.button>
                                )}
                            </div>
                        );
                    })}
                </div>
                {/* Player name label */}
                <div className="absolute bottom-[-22px] left-0 right-0 text-center pointer-events-none">
                    <span className="inline-block px-2 py-0.5 rounded text-white font-display font-extrabold text-[10px] sm:text-xs tracking-wide"
                        style={{ background: t.baseDark }} data-testid={`ludo-base-label-${color}`}>
                        {COLOR_LABELS[color]?.name || t.name}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ---- Centre — non-tilted square with 4 triangles meeting at centre ----
function CenterStar({ activeColors }) {
    const op = (c) => activeColors.includes(c) ? 1 : 0.3;
    return (
        <div className="absolute pointer-events-none" style={{ left: '40%', top: '40%', width: '20%', height: '20%' }}>
            <svg viewBox="0 0 100 100" className="w-full h-full">
                <polygon points="0,0 100,0 50,50"   fill="#E31E24" opacity={op('red')} />
                <polygon points="100,0 100,100 50,50" fill="#3b82f6" opacity={op('blue')} />
                <polygon points="0,100 100,100 50,50" fill="#FFF200" opacity={op('yellow')} />
                <polygon points="0,0 0,100 50,50"   fill="#10b981" opacity={op('green')} />
                <circle cx="50" cy="50" r="14" fill="#fbbf24" stroke="#231F20" strokeWidth="1.5" />
                <text x="50" y="56" textAnchor="middle" fontSize="14" fontWeight="900" fill="#231F20">★</text>
            </svg>
        </div>
    );
}

// ---- Tokens layer (on track) ----
function TokensLayer({ tokens, activeColors, highlightedTokens, onTokenClick, lastMovedToken }) {
    const allTokens = [];
    activeColors.forEach((color) => {
        (tokens[color] || []).forEach((pos, i) => {
            if (pos === 0) return; // in base — drawn by BaseOverlay
            const coord = tokenCoord(color, pos);
            if (!coord) return;
            allTokens.push({ color, idx: i, pos, coord });
        });
    });

    const byCell = new Map();
    allTokens.forEach((tk) => {
        const k = `${tk.coord[0]},${tk.coord[1]}`;
        if (!byCell.has(k)) byCell.set(k, []);
        byCell.get(k).push(tk);
    });

    return (
        <>
            {allTokens.map((tk) => {
                const k = `${tk.coord[0]},${tk.coord[1]}`;
                const stack = byCell.get(k);
                const stackIdx = stack.findIndex((s) => s.color === tk.color && s.idx === tk.idx);
                const stackOffset = stack.length > 1 ? stackIdx * 5 : 0;
                const left = (tk.coord[1] / 15) * 100;
                const top  = (tk.coord[0] / 15) * 100;
                const isHighlighted = highlightedTokens?.color === tk.color && highlightedTokens.indices?.includes(tk.idx);
                const isLast = lastMovedToken?.color === tk.color && lastMovedToken.idx === tk.idx;
                return (
                    <motion.button
                        key={`${tk.color}-${tk.idx}`}
                        layout
                        layoutId={`tk-${tk.color}-${tk.idx}`}
                        onClick={isHighlighted ? () => onTokenClick(tk.color, tk.idx) : undefined}
                        animate={{ x: stackOffset, y: -stackOffset, scale: isHighlighted ? 1.18 : 1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 18, mass: 0.6 }}
                        className={classNames('absolute', isHighlighted && 'cursor-pointer z-30')}
                        style={{
                            left: `calc(${left}% + 1px)`,
                            top:  `calc(${top}% - 4%)`,
                            width:  'calc(100%/15)',
                            height: 'calc(100%/15 * 1.4)',
                            zIndex: 10 + stackIdx + (isHighlighted ? 20 : 0),
                        }}
                        data-testid={`ludo-token-${tk.color}-${tk.idx}`}
                        title={`${COLOR_LABELS[tk.color]?.name} #${tk.idx + 1}`}
                    >
                        <MapPin color={tk.color} highlighted={isHighlighted} isLast={isLast} />
                        {isHighlighted && (
                            <motion.div
                                animate={{ y: [-3, 2, -3] }}
                                transition={{ repeat: Infinity, duration: 0.9 }}
                                className="absolute -top-3 left-1/2 -translate-x-1/2 text-amber-500 text-base sm:text-lg drop-shadow"
                                data-testid={`ludo-hand-token-${tk.color}-${tk.idx}`}>
                                👇
                            </motion.div>
                        )}
                    </motion.button>
                );
            })}
        </>
    );
}

// ---- MapPin token: teardrop with colored ball on top ----
function MapPin({ color, highlighted, isLast }) {
    const t = COLOR_THEME[color];
    return (
        <svg viewBox="0 0 50 70" className="w-full h-full drop-shadow-lg overflow-visible">
            <defs>
                <radialGradient id={`pin-grad-${color}`} cx="40%" cy="35%" r="60%">
                    <stop offset="0%"  stopColor="#ffffff" />
                    <stop offset="55%" stopColor={t.pin} />
                    <stop offset="100%" stopColor={t.pinDark} />
                </radialGradient>
                <radialGradient id={`pin-body-${color}`} cx="50%" cy="20%" r="80%">
                    <stop offset="0%"  stopColor="#ffffff" />
                    <stop offset="40%" stopColor="#f1f5f9" />
                    <stop offset="100%" stopColor="#cbd5e1" />
                </radialGradient>
            </defs>
            {/* Shadow under base */}
            <ellipse cx="25" cy="66" rx="9" ry="2.5" fill="rgba(0,0,0,0.35)" />
            {/* Teardrop body (white/silver) */}
            <path
                d="M25 4 C13 4, 6 12, 6 24 C6 36, 18 50, 25 64 C32 50, 44 36, 44 24 C44 12, 37 4, 25 4 Z"
                fill={`url(#pin-body-${color})`}
                stroke={t.pinDark}
                strokeWidth="1.8"
            />
            {/* Colored ball top */}
            <circle cx="25" cy="22" r="11" fill={`url(#pin-grad-${color})`} stroke={t.pinDark} strokeWidth="1.5" />
            {/* Ball highlight */}
            <ellipse cx="21" cy="18" rx="3.5" ry="2.2" fill="rgba(255,255,255,0.7)" />
            {/* Highlight glow */}
            {highlighted && (
                <circle cx="25" cy="22" r="14" fill="none" stroke="#FFD700" strokeWidth="2">
                    <animate attributeName="stroke-width" values="2;4;2" dur="1s" repeatCount="indefinite" />
                </circle>
            )}
            {isLast && !highlighted && (
                <circle cx="25" cy="22" r="13" fill="none" stroke="#fbbf24" strokeWidth="1.5" opacity="0.7" />
            )}
        </svg>
    );
}
